import React, { useEffect, useState } from "react";

import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Progress,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";

import { countries as countriesData } from "@hz/stealthy-gmc-cloaker-common";
import type { Store } from "@hz/stealthy-gmc-cloaker-common/api-types/store";

const countries = countriesData.countries;
import { PLAN_LIMITS , roles } from "@hz/stealthy-gmc-cloaker-common/constants";

import { Edit2, ExternalLink, MoreVertical, Plus, Power, PowerOff, Search, Trash2 } from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { DisableStoreModal } from "@components/DisableStoreModal";
import { QuotaUsageBar } from "@components/QuotaUsageBar";

import { useGetStoresQuery } from "@state/apiSlice/requests";
import { selectUserState } from "@state/userSlice/userSlice";

import { formatUnixDate } from "@utils/formatDate";

import { DashboardLayout } from "../layouts/DashboardLayout";

export const Stores = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const toast = useToast();
  const { billing, userRole } = useSelector(selectUserState);

  useEffect(() => {
    if (userRole === roles.STANDARD) {
      navigate("/whitelist");
    }
  }, [navigate]);

  const [selectedStore, setSelectedStore] = useState<Store | null>(null);
  const [isDisableDialogOpen, setIsDisableDialogOpen] = useState(false);

  const { data, isLoading } = useGetStoresQuery({}, { skip: userRole === roles.STANDARD });
  const stores = data?.stores || [];

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPlatform, setSelectedPlatform] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");

  const upgradeHandler = async () => {
    navigate("/upgrade");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "ENABLED":
        return "green";
      case "DISABLED":
        return "red";
      default:
        return "gray";
    }
  };

  const getStoreStatus = (store: Store) => {
    return store.disabled ? "DISABLED" : "ENABLED";
  };

  const formatDate = (dateString: Date) =>
    new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });

  // Filtering Logic
  const filteredStores = stores.filter((store) => {
    const matchesSearch = store.domain.toLowerCase().includes(searchQuery.toLowerCase());

    const status = getStoreStatus(store);
    const matchesStatus = selectedStatus === "ALL" || status === selectedStatus;

    return matchesSearch && matchesStatus;
  });

  const getCountryFlag = (countryCode: string) => {
    return `https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`;
  };

  const editHandler = () => {
    toast({
      title: "Coming soon",
      description: "Opening editing options...",
      status: "info",
      duration: 3000,
      position: "bottom-right",
    });
  };

  return (
    <DashboardLayout>
      <Box>
        <Flex
          justify="space-between"
          align="center"
          mb={8}
        >
          <Heading size="lg">My Stores</Heading>
          <Button
            leftIcon={<Plus size={20} />}
            onClick={() => navigate("/stores/new")}
            bg="#ff3d00"
            color="white"
            _hover={{
              bg: "#e53700",
              boxShadow: "0 0 20px rgba(255, 61, 0, 0.5)",
            }}
          >
            Add Store
          </Button>
        </Flex>

        {billing?.plan && billing?.sessionCount > 0 && (
          <QuotaUsageBar
            current={billing?.sessionCount ?? 0}
            limit={PLAN_LIMITS[billing?.plan]}
            currentPlan={billing?.plan}
            size="lg"
            onUpgrade={upgradeHandler}
          />
        )}

        {/* Filters */}
        <HStack
          spacing={4}
          mb={6}
        >
          <InputGroup maxW="320px">
            <InputLeftElement pointerEvents="none">
              <Search
                size={20}
                color="gray"
              />
            </InputLeftElement>
            <Input
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="gray.800"
              border="1px"
              borderColor="gray.700"
              _placeholder={{ color: "gray.400" }}
            />
          </InputGroup>

          <Select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            bg="gray.800"
            border="1px"
            borderColor="gray.700"
            w="200px"
          >
            <option value="ALL">All Status</option>
            <option value="ENABLED">Enabled</option>
            <option value="DISABLED">Disabled</option>
          </Select>
        </HStack>

        {/* Table */}
        {isLoading ? (
          <Text color="gray.400">Loading stores...</Text>
        ) : filteredStores.length === 0 ? (
          <Text color="gray.400">No stores found.</Text>
        ) : (
          <TableContainer
            bg="gray.800"
            borderRadius="lg"
            border="1px"
            borderColor="gray.700"
            sx={{
              "& th": {
                borderBottom: "1px solid",
                borderColor: "gray.700",
                color: "gray.400",
                fontSize: "sm",
              },
              "& td": {
                borderBottom: "1px solid",
                borderColor: "gray.700",
              },
              "& tr:last-of-type td": {
                borderBottom: "none",
              },
            }}
          >
            <Table variant="unstyled">
              <Thead>
                <Tr>
                  <Th>Store Domain</Th>
                  <Th>Platform</Th>
                  <Th>Added</Th>
                  <Th>Countries</Th>
                  <Th>MONTHLY SESSIONS</Th>
                  <Th>Status</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredStores.map((store) => (
                  <Tr
                    key={store.storeId}
                    _hover={{ bg: "gray.700" }}
                  >
                    <Td>
                      <Text color="white">{store.domain}</Text>
                    </Td>
                    <Td>
                      <Text color="gray.400">{store.platform || "—"}</Text>
                    </Td>
                    <Td>
                      <Text color="gray.500">{formatDate(store.createdAt)}</Text>
                    </Td>
                    <Td>
                      <Wrap spacing={1}>
                        {store.countriesServed.map((code) => (
                          <WrapItem key={code}>
                            <Tooltip
                              label={countries.find((c) => c.code === code)?.name || code}
                              placement="top"
                            >
                              <Box
                                as="img"
                                src={getCountryFlag(code)}
                                alt={code}
                                w="24px"
                                h="18px"
                                borderRadius="2px"
                                border="1px solid"
                                borderColor="gray.700"
                                opacity={store.disabled ? 0.6 : 1}
                                transition="opacity 0.2s"
                                _hover={{
                                  opacity: 1,
                                }}
                              />
                            </Tooltip>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </Td>
                    <Td>
                      <VStack
                        spacing={0}
                        align="flex-start"
                      >
                        <Text color="white">{store.sessionsThisMonth}</Text>
                        <Text
                          color="gray.500"
                          fontSize="xs"
                        >
                          {`Period starting 1st of ${store.yearMonth}`}
                          {/* {`${formatUnixDate(store.sessionPeriod.start, "en-US", { showYear: false })} – ${formatUnixDate(store.sessionPeriod.end, "en-US")}`} */}
                        </Text>
                      </VStack>
                    </Td>
                    <Td>
                      <Badge
                        colorScheme={getStatusColor(getStoreStatus(store))}
                        variant="subtle"
                      >
                        {getStoreStatus(store)}
                      </Badge>
                    </Td>
                    <Td>
                      <Menu>
                        <MenuButton
                          as={Button}
                          variant="ghost"
                          size="sm"
                          color="gray.400"
                          _hover={{ bg: "gray.700" }}
                        >
                          <MoreVertical size={16} />
                        </MenuButton>

                        <MenuList
                          bg="black"
                          borderColor="gray.700"
                        >
                          <MenuItem
                            icon={<ExternalLink size={16} />}
                            color="white"
                            bg="black"
                            _hover={{ bg: "gray.700" }}
                            onClick={() => window.open(`https://${store.domain}`, "_blank")}
                          >
                            View Store
                          </MenuItem>
                          {/* <MenuItem 
                          icon={<Edit2 size={16} />} 
                          color="white" 
                          bg="black" 
                          _hover={{ bg: "gray.700" }}
                          onClick={editHandler}
                          >
                            Edit
                          </MenuItem> */}
                          <MenuItem
                            icon={store.disabled ? <Power size={16} /> : <PowerOff size={16} />}
                            color={store.disabled ? "green.400" : "yellow.400"}
                            bg="black"
                            _hover={{ bg: "gray.700" }}
                            onClick={() => {
                              setSelectedStore(store);
                              setIsDisableDialogOpen(true);
                            }}
                          >
                            {store.disabled ? "Enable Store" : "Disable Store"}
                          </MenuItem>
                        </MenuList>
                      </Menu>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <DisableStoreModal
        selectedStore={selectedStore}
        isDisableDialogOpen={isDisableDialogOpen}
        setIsDisableDialogOpen={setIsDisableDialogOpen}
      />
    </DashboardLayout>
  );
};
